import React, {useEffect, useState} from "react";
import {grey} from "@mui/material/colors";
import {useUserPrefString} from "../../../../../misc/userpref/useUserPref";
import {useProjectId} from "../../ProjectName";
import {useAlternative} from "../../../quote/alternative/Alternative";
import {Box, IconButton, Tooltip, Typography} from "@mui/material";
import KeyboardDoubleArrowRight from "@mui/icons-material/KeyboardDoubleArrowRight";
import {cellFocusShowable, ElevationCellFocus, useElevationCellFocusEmitter, useElevationSidebarOpen} from "./ElevationSidebarContext";
import {SidebarTab, sidebarTabLabel, sidebarTabWorkspace} from "./useSidebarElevationApp";
import {ElevationSidebarStrip} from "./ElevationSidebarStrip";
import {ElevationSidebarHeader} from "./ElevationSidebarHeader";
import {ElevationSidebarDrawing} from "./ElevationSidebarDrawing";
import {ElevationSidebarFile} from "./ElevationSidebarFile";

export const SIDEBAR_WIDTH = 460;

// "auto": the sidebar opens when an elevation cell is focused. "manual": it stays closed and
// the strip on the right re-opens it.
const modePref = "openings.elevationSidebar.mode";

// the right sidebar of the openings tables: a view-only preview of the focused opening's
// screen / door / hardware elevation and its frame anchors, with a link into the elevation
// tool. A legacy file-based elevation shows its file. Rendered beside the table, inside an
// ElevationSidebarProvider.
export function ElevationSidebar(props: {
    // the quote openings table: previews load from the quote opening in the route's alternative.
    quote?: boolean;
}) {
    const project = useProjectId();
    const alternative = useAlternative();
    const emitter = useElevationCellFocusEmitter();
    const {setOpen} = useElevationSidebarOpen();
    const [mode, setMode] = useUserPrefString(modePref, "auto");

    // the last elevation cell the user focused that has something to show (the preview stays on
    // it once the cell blurs, e.g. when they click the drawing), and the focused cell right now
    // when it has something to show.
    const [current, setCurrent] = useState<ElevationCellFocus | null>(null);
    const [focused, setFocused] = useState<ElevationCellFocus | null>(null);
    const [tab, setTab] = useState<SidebarTab>("screen");

    useEffect(() => {
        if(!emitter) return;
        const sub = emitter.subscribeAndFireLast(focus => {
            // a blank cell has nothing to show: hide.
            const showable = focus && cellFocusShowable(focus) ? focus : null;
            setFocused(showable);
            if(focus) {
                setCurrent(showable);
                if(focus.type === "elevation") setTab(focus.tab);
            }
        });
        return () => sub.cancel();
    }, [emitter]);

    const open = mode === "auto" && !!current;
    useEffect(() => {
        setOpen(open);
        return () => setOpen(false);
    }, [open, setOpen]);

    if(mode !== "auto") {
        return <ElevationSidebarStrip focus={focused} onOpen={() => setMode("auto")} />;
    }

    if(!current) return null;

    return (
        <ElevationSidebarPanel
            project={project}
            alternative={props.quote ? alternative : undefined}
            focus={current}
            tab={tab}
            onTabChange={setTab}
            onCollapse={() => setMode("manual")}
        />
    );
}

function ElevationSidebarPanel(props: {
    project: number;
    alternative?: number;
    focus: ElevationCellFocus;
    tab: SidebarTab;
    onTabChange(tab: SidebarTab): void;
    onCollapse(): void;
}) {
    const focus = props.focus;
    const panel: React.CSSProperties = {
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        borderLeft: "1px solid " + grey["300"],
        backgroundColor: "white",
    };

    // a cell with a file: the opening and the cell in the header, the file under it.
    if(focus.type === "file") {
        return (
            <div style={panel}>
                <Box sx={{display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.5, bgcolor: grey["800"], color: "#fff", flexShrink: 0}}>
                    <Typography sx={{fontSize: 13, fontWeight: 600}} noWrap>{focus.openingName || `Opening ${focus.openingId}`}</Typography>
                    <Typography sx={{fontSize: 12, color: "rgba(255,255,255,0.75)", flex: 1}} noWrap>{focus.cell}</Typography>
                    <Tooltip title="Hide the preview">
                        <IconButton size="small" onClick={props.onCollapse} sx={{color: "#fff"}}>
                            <KeyboardDoubleArrowRight fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <ElevationSidebarFile label={focus.cell} openingName={focus.openingName} name={focus.name} files={focus.files} />
            </div>
        );
    }

    const kind = sidebarTabWorkspace(props.tab);
    const source = focus.sources[kind];

    return (
        <div style={panel}>
            <ElevationSidebarHeader
                tab={props.tab}
                onTabChange={props.onTabChange}
                onCollapse={props.onCollapse}
                anchorsDisabled={focus.sources.screen?.type !== "drw"}
            />
            {source?.type === "drw" ? (
                <ElevationSidebarDrawing
                    project={props.project}
                    alternative={props.alternative}
                    openingId={focus.openingId}
                    openingName={focus.openingName}
                    fingerprint={focus.fingerprint}
                    tab={props.tab}
                />
            ) : source?.type === "file" ? (
                <ElevationSidebarFile
                    label={`${sidebarTabLabel(kind)} elevation (file)`}
                    openingName={focus.openingName}
                    name={source.name}
                    files={source.files}
                />
            ) : (
                <div style={{padding: 12, fontSize: 12, color: grey["600"]}}>
                    {focus.openingName || `Opening ${focus.openingId}`} has no {kind} elevation.
                </div>
            )}
        </div>
    );
}
